import { TFunction } from 'i18next';
import {
  LucideIcon,
  BotMessageSquare,
  LayoutDashboard,
  MessagesSquare,
  Users,
  AlertTriangle,
  LayoutGrid,
  BookOpen,
  ClipboardList,
  ListChecks,
  GraduationCap,
  Wallet,
  FileText,
  Receipt,
  CalendarDays,
  UserRound,
  Video,
  BarChart3,
  UserPlus,
  FolderOpen,
  Plus,
  Briefcase,
  Building2,
  Landmark,
  Lightbulb,
  TrendingUp,
  FileSpreadsheet,
  FileSignature,
  FilePlus,
  Link2,
  Magnet,
  Megaphone,
  MessageSquareText,
  Settings,
  Sparkles,
} from 'lucide-react';

import { PATHS } from 'router/config';
import { PopupNames } from 'core';
import { UserRole } from 'types';
import {
  visibleFor,
  AI_AGENT_ROLES,
  PROFILE_ROLES,
  STUDENT_ROLES,
  COMMUNICATOR_ROLES,
  TASK_TRACKER_ROLES,
  EDUCATION_ROLES,
  TEACHER_PROFILE_ROLES,
  EDUCATION_SECTION_ROLES,
  FINANCE_VIEW_ROLES,
  FINANCE_EXPENSE_ROLES,
  FINANCE_PLAN_ROLES,
  ADMIN_ROLES,
  ZOOM_ROLES,
  SURVEY_ROLES,
  CAREER_ROLES,
  PERFORMANCE_ROLES,
  DOFIN_ROLES,
  ESIGN_PAGE_ROLES,
  ESIGN_STUDENT_WRITE_ROLES,
  LEAD_ROLES,
  MARKETING_ROLES,
  SHORT_LINK_ROLES,
  SMS_ROLES,
  SETTINGS_HUB_ROLES,
  SETTINGS_SECTIONS,
  settingsCommandId,
  SETTINGS_DOMAINS,
  SETTINGS_DOMAIN_ICONS,
  settingsDomainCommandId,
  settingsDomainHref,
  settingsDomainRoles,
  withContractSurface,
} from 'core/hooks';

import { ICommand, IPaletteRunContext } from './paletteTypes';

interface ICommandDef {
  id: string;
  allowed?: UserRole[];
  /**
   * За флагом сервера — как пункт меню (ТЗ 0076: `ESIGN_ENABLED`; ТЗ 0118:
   * `kbReview` — рецензент базы знаний без Admin).
   */
  feature?: 'esign' | 'kbReview';
  icon: LucideIcon;
  keywords: string[];
  run: (ctx: IPaletteRunContext) => void;
}

/**
 * The full command registry — navigation destinations + global «create» flows.
 * Titles resolve from `commandPalette.commands.<id>`; `keywords` are literal
 * ru/pl/en fuzzy-match aliases (not i18n leaves). Every def is role-gated so the
 * palette never offers a page the operator cannot reach.
 */
const COMMAND_DEFS: ICommandDef[] = [
  // --- Navigation --------------------------------------------------------
  {
    id: 'dashboard',
    allowed: TASK_TRACKER_ROLES,
    icon: LayoutDashboard,
    // ТЗ 0096: страница переименована в «Таск-трекер» — старые слова
    // остаются, чтобы привычный поиск тоже находил.
    keywords: [
      'dashboard',
      'дашборд',
      'главная',
      'панель',
      'pulpit',
      'kokpit',
      'таск',
      'трекер',
      'задачи',
      'task',
      'tracker',
      'zadania',
    ],
    run: (ctx) => ctx.history.push(PATHS.dashboard),
  },
  {
    id: 'communicator',
    allowed: COMMUNICATOR_ROLES,
    icon: MessagesSquare,
    keywords: [
      'communicator',
      'коммуникатор',
      'сообщения',
      'чат',
      'komunikator',
      'wiadomości',
    ],
    run: (ctx) => ctx.history.push(PATHS.communicator),
  },
  {
    id: 'students',
    allowed: STUDENT_ROLES,
    icon: Users,
    keywords: ['students', 'студенты', 'ученики', 'studenci', 'uczniowie'],
    run: (ctx) => ctx.history.push(PATHS.students),
  },
  {
    id: 'dofinansowanie',
    allowed: DOFIN_ROLES,
    icon: Landmark,
    keywords: [
      'dofinansowanie',
      'дофинансирование',
      'софинансирование',
      'bur',
      'kfs',
      'воронка',
      'lejek',
      'operatorzy',
      'операторы',
    ],
    run: (ctx) => ctx.history.push(PATHS.dofinansowanie),
  },
  {
    // ТЗ 0084. Роли — тот же список, что у пункта меню и маршрута.
    id: 'leads',
    allowed: LEAD_ROLES,
    icon: Magnet,
    keywords: [
      'leads',
      'kommo',
      'amocrm',
      'лиды',
      'лид',
      'crm',
      'leady',
      'lead',
      'new leads',
    ],
    run: (ctx) => ctx.history.push(PATHS.leads),
  },
  {
    // ТЗ 0088. Роли — тот же список, что у пункта меню и маршрута.
    id: 'marketing',
    allowed: MARKETING_ROLES,
    icon: Megaphone,
    keywords: [
      'marketing',
      'маркетинг',
      'реклама',
      'каналы',
      'meta',
      'facebook',
      'google ads',
      'roas',
      'cpl',
      'reklama',
      'kanały',
      'kampanie',
    ],
    run: (ctx) => ctx.history.push(PATHS.marketing),
  },
  {
    // ТЗ 0076. Роли — тот же список, что у пункта меню и маршрута.
    id: 'contracts',
    allowed: ESIGN_PAGE_ROLES,
    feature: 'esign',
    icon: FileSignature,
    keywords: [
      'contracts',
      'договоры',
      'договор',
      'подпись',
      'documenso',
      'umowy',
      'umowa',
      'podpis',
    ],
    run: (ctx) => ctx.history.push(PATHS.contracts),
  },
  {
    // ТЗ 0080. Без флага `esign`: сократитель от подписи не зависит. Роли —
    // тот же список, что у пункта меню и маршрута.
    id: 'shortLinks',
    allowed: SHORT_LINK_ROLES,
    icon: Link2,
    keywords: [
      'short links',
      'shortener',
      'короткие ссылки',
      'сокращатель',
      'ссылка',
      'переходы',
      'krótkie linki',
      'skracacz',
      'link',
    ],
    run: (ctx) => ctx.history.push(PATHS.shortLinks),
  },
  {
    // ТЗ 0081. Роли — тот же список, что у пункта меню и маршрута.
    id: 'sms',
    allowed: SMS_ROLES,
    icon: MessageSquareText,
    keywords: [
      'sms',
      'смс',
      'эсэмэс',
      'рассылка',
      'напоминания',
      'smsapi',
      'wiadomości',
      'przypomnienia',
    ],
    run: (ctx) => ctx.history.push(PATHS.sms),
  },
  {
    id: 'debtors',
    allowed: STUDENT_ROLES,
    icon: AlertTriangle,
    keywords: [
      'debtors',
      'должники',
      'долги',
      'задолженность',
      'dłużnicy',
      'zadłużeni',
    ],
    run: (ctx) => ctx.history.push(PATHS.debtors),
  },
  {
    id: 'groups',
    // ТЗ 0093: те же роли, что у маршрута «Обучения» и пункта меню (с
    // методистом); правка групп — по-прежнему `EDUCATION_ROLES`.
    allowed: EDUCATION_SECTION_ROLES,
    icon: LayoutGrid,
    keywords: ['groups', 'группы', 'grupy'],
    run: (ctx) => ctx.history.push(PATHS.settingsGroups),
  },
  {
    id: 'courses',
    // ТЗ 0093: те же роли, что у маршрута «Обучения» и пункта меню (с
    // методистом); правка групп — по-прежнему `EDUCATION_ROLES`.
    allowed: EDUCATION_SECTION_ROLES,
    icon: BookOpen,
    keywords: ['courses', 'курсы', 'kursy'],
    run: (ctx) => ctx.history.push(PATHS.settingsCourses),
  },
  {
    id: 'journals',
    // ТЗ 0093: те же роли, что у маршрута «Обучения» и пункта меню (с
    // методистом); правка групп — по-прежнему `EDUCATION_ROLES`.
    allowed: EDUCATION_SECTION_ROLES,
    icon: ClipboardList,
    keywords: [
      'journals',
      'журналы',
      'посещаемость',
      'dzienniki',
      'frekwencja',
    ],
    run: (ctx) => ctx.history.push(PATHS.settingsJournals),
  },
  {
    id: 'teachers',
    // Тот же список, что у маршрута и меню (`TEACHER_PROFILE_ROLES`).
    allowed: TEACHER_PROFILE_ROLES,
    icon: GraduationCap,
    keywords: [
      'teachers',
      'преподаватели',
      'учителя',
      'nauczyciele',
      'wykładowcy',
    ],
    run: (ctx) => ctx.history.push(PATHS.teachers),
  },
  {
    id: 'finances',
    allowed: FINANCE_VIEW_ROLES,
    icon: Wallet,
    keywords: ['finances', 'финансы', 'деньги', 'finanse', 'pieniądze'],
    run: (ctx) => ctx.history.push(PATHS.financesDashboard),
  },
  {
    id: 'invoices',
    allowed: FINANCE_VIEW_ROLES,
    icon: FileText,
    keywords: ['invoices', 'счета', 'фактуры', 'faktury', 'rachunki'],
    run: (ctx) => ctx.history.push(PATHS.financesInvoices),
  },
  {
    id: 'expenses',
    allowed: FINANCE_EXPENSE_ROLES,
    icon: Receipt,
    keywords: ['expenses', 'расходы', 'траты', 'wydatki', 'koszty'],
    run: (ctx) => ctx.history.push(PATHS.financesExpenses),
  },
  {
    id: 'paymentCalendar',
    allowed: FINANCE_PLAN_ROLES,
    icon: CalendarDays,
    keywords: [
      'payment calendar',
      'платежный календарь',
      'календарь платежей',
      'kalendarz płatności',
    ],
    run: (ctx) => ctx.history.push(PATHS.financesPaymentCalendar),
  },
  {
    id: 'cashFlow',
    allowed: FINANCE_PLAN_ROLES,
    icon: TrendingUp,
    keywords: [
      'cash flow',
      'cashflow',
      'кэшфлоу',
      'денежный поток',
      'оддс',
      'przepływy pieniężne',
      'rpp',
    ],
    run: (ctx) => ctx.history.push(PATHS.financesCashFlow),
  },
  {
    id: 'pnlExport',
    allowed: FINANCE_PLAN_ROLES,
    icon: FileSpreadsheet,
    keywords: [
      'p&l',
      'pnl',
      'прибыли и убытки',
      'выгрузка',
      'признание выручки',
      'zyski i straty',
      'eksport',
    ],
    run: (ctx) => ctx.history.push(PATHS.financesPnl),
  },
  {
    // ТЗ 0093: хаб настроек. Роли — объединение ролей разделов реестра, тот
    // же список у маршрута и пункта меню «Все настройки».
    id: 'settings',
    allowed: SETTINGS_HUB_ROLES,
    icon: Settings,
    keywords: [
      'settings',
      'настройки',
      'конфигурация',
      'параметры',
      'ustawienia',
      'konfiguracja',
    ],
    run: (ctx) => ctx.history.push(PATHS.settings),
  },
  // ТЗ 0093: разделы хаба — командами прямо из реестра: название, значок,
  // роли и адрес у команды те же, что у карточки в хабе. Карточки-ссылки
  // (`kind: 'link'`) — свои разделы со своими командами («Пользователи»).
  ...SETTINGS_SECTIONS.filter((section) => section.kind === 'section').map(
    (section): ICommandDef => ({
      id: settingsCommandId(section.id),
      allowed: section.roles,
      icon: section.icon,
      keywords: section.keywords,
      run: (ctx) => ctx.history.push(section.to),
    }),
  ),
  // ТЗ 0095: «Настройки: Маркетинг» и т. д. — хаб на смысловом разделе.
  // Роли — объединение ролей карточек домена (как у чипа в хабе). Слова
  // карточек сюда не копируются: их уже находят команды самих разделов.
  ...SETTINGS_DOMAINS.map(
    (domain): ICommandDef => ({
      id: settingsDomainCommandId(domain),
      allowed: settingsDomainRoles(domain),
      icon: SETTINGS_DOMAIN_ICONS[domain],
      keywords: [
        'настройки',
        'ustawienia',
        'settings',
        'раздел',
        'dział',
        domain,
      ],
      run: (ctx) => ctx.history.push(settingsDomainHref(domain)),
    }),
  ),
  {
    id: 'users',
    allowed: ADMIN_ROLES,
    icon: UserRound,
    keywords: [
      'users',
      'пользователи',
      'персонал',
      'сотрудники',
      'użytkownicy',
      'personel',
    ],
    run: (ctx) => ctx.history.push(PATHS.users),
  },
  {
    id: 'zoom',
    allowed: ZOOM_ROLES,
    icon: Video,
    keywords: ['zoom', 'видео', 'записи', 'вебинары', 'nagrania', 'wideo'],
    run: (ctx) => ctx.history.push(PATHS.zoomDashboard),
  },
  {
    // ТЗ 0104. Роли — тот же список, что у пункта меню и маршрута (Admin).
    id: 'aiAgents',
    allowed: AI_AGENT_ROLES,
    icon: BotMessageSquare,
    keywords: [
      'ai',
      'ии',
      'агент',
      'ии-агент',
      'нейросеть',
      'deepseek',
      'agent',
      'agent ai',
      'sztuczna inteligencja',
      'czat',
      'baza wiedzy',
      'база знаний',
    ],
    run: (ctx) => ctx.history.push(PATHS.aiAgents),
  },
  {
    // ТЗ 0106: ИИ-чат на весь экран — те же роли, что у кнопки в шапке и
    // маршрута (`AI_AGENT_ROLES`).
    id: 'aiChat',
    allowed: AI_AGENT_ROLES,
    icon: Sparkles,
    keywords: [
      'ai',
      'ии',
      'чат',
      'ии-чат',
      'спросить',
      'ассистент',
      'czat',
      'czat ai',
      'asystent',
      'zapytaj',
      'chat',
      'claude',
      'deepseek',
    ],
    run: (ctx) => ctx.history.push(PATHS.aiChat),
  },
  {
    // ТЗ 0118 B4: очередь предложений в базу знаний — рецензенту без Admin
    // (как пункт меню: все роли ERP + назначения, `feature: 'kbReview'`).
    id: 'aiKbReview',
    allowed: PROFILE_ROLES,
    feature: 'kbReview',
    icon: Lightbulb,
    keywords: [
      'baza wiedzy',
      'propozycje',
      'recenzja',
      'база знаний',
      'предложения',
      'рецензент',
      'knowledge',
    ],
    run: (ctx) => ctx.history.push(PATHS.aiKbReview),
  },
  {
    id: 'surveys',
    allowed: SURVEY_ROLES,
    icon: ListChecks,
    keywords: [
      'surveys',
      'опросы',
      'опрос',
      'анкеты',
      'анкета',
      'ankiety',
      'ankieta',
    ],
    run: (ctx) => ctx.history.push(PATHS.surveys),
  },
  {
    id: 'careerCenter',
    allowed: CAREER_ROLES,
    icon: Briefcase,
    keywords: [
      'career',
      'career center',
      'карьера',
      'карьерный центр',
      'трудоустройство',
      'вакансии',
      'kariera',
      'centrum kariery',
      'zatrudnienie',
    ],
    run: (ctx) => ctx.history.push(PATHS.careerCenter),
  },
  // ТЗ 0040 доводка: шесть справочников были доступны ТОЛЬКО набором адреса.
  // В палитре они отдельными командами, а не одной: их ищут по имени
  // сущности — «медали», «профессии», «анкеты», — а не по слову «справочник».
  {
    id: 'careerSkills',
    allowed: CAREER_ROLES,
    icon: Briefcase,
    keywords: ['навыки', 'справочник навыков', 'skills', 'umiejętności'],
    run: (ctx) => ctx.history.push(PATHS.careerSkills),
  },
  {
    id: 'careerProfessions',
    allowed: CAREER_ROLES,
    icon: Briefcase,
    keywords: ['профессии', 'треки', 'professions', 'zawody', 'ścieżki'],
    run: (ctx) => ctx.history.push(PATHS.careerProfessions),
  },
  {
    id: 'careerMedals',
    allowed: CAREER_ROLES,
    icon: Briefcase,
    keywords: ['медали', 'ачивки', 'очки', 'medals', 'odznaki', 'punkty'],
    run: (ctx) => ctx.history.push(PATHS.careerMedals),
  },
  {
    id: 'careerSiteCourses',
    allowed: CAREER_ROLES,
    icon: Briefcase,
    keywords: ['курсы сайта', 'витрина', 'продажа', 'site courses', 'kursy'],
    run: (ctx) => ctx.history.push(PATHS.careerSiteCourses),
  },
  {
    id: 'careerLessons',
    allowed: CAREER_ROLES,
    icon: Briefcase,
    keywords: ['уроки центра', 'обучение', 'career lessons', 'zajęcia centrum'],
    // Уроки и анкеты вернулись вкладками карьерного центра. Ведём палитру
    // сразу на вкладку, а не через редирект со старого адреса: лишний прыжок
    // видно глазом, и в истории браузера остаётся мусорный шаг.
    run: (ctx) => ctx.history.push(`${PATHS.careerCenter}?tab=lessons`),
  },
  {
    id: 'careerExercises',
    allowed: CAREER_ROLES,
    icon: Briefcase,
    keywords: ['анкеты', 'анкеты уроков', 'exercises', 'ankiety lekcji'],
    run: (ctx) => ctx.history.push(`${PATHS.careerCenter}?tab=exercises`),
  },
  {
    id: 'performance',
    allowed: PERFORMANCE_ROLES,
    icon: BarChart3,
    keywords: [
      'performance',
      'успеваемость',
      'показатели',
      'эффективность',
      'wyniki',
      'efektywność',
    ],
    run: (ctx) => ctx.history.push(PATHS.performance),
  },
  // --- Global «create» ---------------------------------------------------
  {
    id: 'newStudent',
    allowed: STUDENT_ROLES,
    icon: UserPlus,
    keywords: [
      'new student',
      'add student',
      'создать студента',
      'добавить студента',
      'новый студент',
      'nowy uczeń',
      'dodaj ucznia',
    ],
    run: (ctx) => ctx.showPopup(PopupNames.addStudent),
  },
  {
    id: 'newGroup',
    allowed: EDUCATION_ROLES,
    icon: FolderOpen,
    keywords: [
      'new group',
      'add group',
      'создать группу',
      'добавить группу',
      'новая группа',
      'nowa grupa',
      'dodaj grupę',
    ],
    run: (ctx) => ctx.showPopup(PopupNames.addGroup),
  },
  {
    id: 'newTeacher',
    allowed: EDUCATION_ROLES,
    icon: GraduationCap,
    keywords: [
      'new teacher',
      'add teacher',
      'создать преподавателя',
      'добавить преподавателя',
      'nowy nauczyciel',
      'dodaj nauczyciela',
    ],
    run: (ctx) => ctx.showPopup(PopupNames.teacherAddPopup),
  },
  {
    id: 'newExpense',
    allowed: FINANCE_EXPENSE_ROLES,
    icon: Plus,
    keywords: [
      'new expense',
      'add expense',
      'создать расход',
      'добавить расход',
      'nowy wydatek',
      'dodaj wydatek',
    ],
    run: (ctx) => ctx.showPopup(PopupNames.expenseForm, {}),
  },
  {
    // ТЗ 0076. Ящик «Новый договор» живёт в адресе раздела — палитра просто
    // ведёт туда, как и кнопка на карточке группы.
    id: 'newContract',
    allowed: ESIGN_STUDENT_WRITE_ROLES,
    feature: 'esign',
    // Не FileSignature: он у раздела «Договоры», а это действие «создать».
    icon: FilePlus,
    keywords: [
      'new contract',
      'новый договор',
      'создать договор',
      'договор на подпись',
      'nowa umowa',
      'utwórz umowę',
      'umowa do podpisu',
    ],
    run: (ctx) => openContractSurface(ctx, 'contractNew'),
  },
  {
    // ТЗ 0076. Договор с фирмой-клиентом (B2B) — та же страница, свой ящик.
    id: 'newCompanyContract',
    allowed: ESIGN_STUDENT_WRITE_ROLES,
    feature: 'esign',
    icon: Building2,
    keywords: [
      'company contract',
      'b2b',
      'договор с фирмой',
      'корпоративный договор',
      'umowa z firmą',
      'umowa b2b',
    ],
    run: (ctx) => openContractSurface(ctx, 'companyNew'),
  },
];

/**
 * Окно раздела «Договоры» из палитры. На самой странице договоров фильтры
 * списка (они в адресе) сохраняются; с другой страницы — чистый список.
 */
function openContractSurface(ctx: IPaletteRunContext, key: string) {
  const onPage = window.location.pathname === PATHS.contracts;
  const search = withContractSurface(onPage ? window.location.search : '', {
    [key]: '1',
  });
  ctx.history.push({ pathname: PATHS.contracts, search });
}

/**
 * Build the role-visible command list; titles come from i18n. Commands behind
 * a server flag (`feature`) show only when that flag is on.
 */
export const buildCommands = (
  roles: UserRole[],
  t: TFunction,
  features: { esign?: boolean; kbReview?: boolean } = {},
): ICommand[] =>
  COMMAND_DEFS.filter(
    (def) =>
      visibleFor(roles, def.allowed) &&
      (!def.feature || Boolean(features[def.feature])),
  ).map((def) => ({
    id: def.id,
    title: t(`commandPalette.commands.${def.id}`),
    icon: def.icon,
    keywords: def.keywords,
    run: def.run,
  }));

/**
 * Fuzzy score of a lowercased `query` against a single `text`. Exact > prefix >
 * substring > subsequence; `0` = no match. Shared by the command ranker and the
 * page-action section so both rank the same way (webpack 4: no external lib).
 */
export const scoreText = (query: string, text: string): number => {
  const value = text.toLowerCase();
  if (value === query) return 100;
  if (value.startsWith(query)) return 80;
  const idx = value.indexOf(query);
  if (idx >= 0) return 55 - Math.min(idx, 20);
  // Subsequence fallback (fuzzy) — cheap, no external library (webpack 4).
  let qi = 0;
  for (let i = 0; i < value.length && qi < query.length; i += 1) {
    if (value[i] === query[qi]) qi += 1;
  }
  return qi === query.length ? 20 : 0;
};

/**
 * Rank a command against the raw query over its title + keywords. `0` means no
 * match (drop it); an empty query keeps everything at a flat baseline.
 */
export const scoreCommand = (rawQuery: string, command: ICommand): number => {
  const query = rawQuery.trim().toLowerCase();
  if (!query) return 1;
  return [command.title, ...command.keywords].reduce(
    (best, term) => Math.max(best, scoreText(query, term)),
    0,
  );
};
